import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Container, Form, Button, Row, Col, Card, Spinner, Alert, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PlusCircle, XCircle, Trash } from "react-bootstrap-icons";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import PrintItemService from "../../services/printitem.service";
import UploadLogo from "../modals/UploadLogo";

const apiUrl = process.env.REACT_APP_LOCALHOST_URL;

const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.includes('uploads')) return apiUrl + url;
    return url;
};

const PRICING_TYPES = {
    FLAT: 1,
    QUANTITY: 2,
    BILL_LATER: 3
};

const AdminPrintItemForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const { register, setValue, handleSubmit, formState: { errors } } = useForm();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [file, setFile] = useState(null);
    const [htmlDescription, setHtmlDescription] = useState('');
    const [currentImageUrl, setCurrentImageUrl] = useState('');
    const [active, setActive] = useState(true);

    const [pricingType, setPricingType] = useState(PRICING_TYPES.FLAT);
    const [quantityPrices, setQuantityPrices] = useState([{ quantity: 1, price: 0 }]);

    const [additionalImages, setAdditionalImages] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [deletingImageId, setDeletingImageId] = useState(null);

    const [artworkFiles, setArtworkFiles] = useState([]);
    const [existingArtworks, setExistingArtworks] = useState([]);
    const [deletingArtworkId, setDeletingArtworkId] = useState(null);

    useEffect(() => {
        if (isEditMode) {
            loadPrintItem();
        }
    }, [id]);

    const loadPrintItem = async () => {
        try {
            setLoading(true);
            const item = await PrintItemService.getPrintItemById(id);
            setValue('title', item.title || '');
            setValue('description', item.description || '');
            setValue('code', item.code || '');
            setValue('price', item.price || 0);
            setValue('cost', item.cost || 0);
            setHtmlDescription(item.htmlDescription || '');
            setCurrentImageUrl(item.imageUrl || '');
            setActive(item.active !== false);
            setPricingType(item.pricingType || PRICING_TYPES.FLAT);

            if (item.print_item_quantity_prices && item.print_item_quantity_prices.length > 0) {
                setQuantityPrices(item.print_item_quantity_prices.map(qp => ({
                    quantity: qp.quantity,
                    price: qp.price
                })));
            }

            if (item.print_item_images && item.print_item_images.length > 0) {
                setExistingImages(item.print_item_images);
            }

            if (item.print_item_artworks && item.print_item_artworks.length > 0) {
                setExistingArtworks(item.print_item_artworks);
            }
        } catch (err) {
            console.error("Error loading print item:", err);
            setError("Failed to load print item");
        } finally {
            setLoading(false);
        }
    };

    const addQuantityPrice = () => {
        setQuantityPrices([...quantityPrices, { quantity: 1, price: 0 }]);
    };

    const removeQuantityPrice = (index) => {
        setQuantityPrices(quantityPrices.filter((_, i) => i !== index));
    };

    const updateQuantityPrice = (index, field, value) => {
        const updated = [...quantityPrices];
        updated[index][field] = field === 'quantity' ? parseInt(value) || 0 : parseFloat(value) || 0;
        setQuantityPrices(updated);
    };

    const handleAdditionalImagesChange = (e) => {
        const files = Array.from(e.target.files);
        setAdditionalImages([...additionalImages, ...files]);
    };

    const removeAdditionalImage = (index) => {
        setAdditionalImages(additionalImages.filter((_, i) => i !== index));
    };

    const deleteExistingImage = async (imageId) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;
        try {
            setDeletingImageId(imageId);
            await PrintItemService.deletePrintItemImage(id, imageId);
            setExistingImages(existingImages.filter(img => img.id !== imageId));
        } catch (err) {
            console.error("Error deleting image:", err);
            setError("Failed to delete image");
        } finally {
            setDeletingImageId(null);
        }
    };

    const handleArtworkFilesChange = (e) => {
        const files = Array.from(e.target.files);
        setArtworkFiles([...artworkFiles, ...files]);
        e.target.value = '';
    };

    const removeArtworkFile = (index) => {
        setArtworkFiles(artworkFiles.filter((_, i) => i !== index));
    };

    const deleteExistingArtwork = async (artworkId) => {
        if (!window.confirm('Are you sure you want to delete this artwork file?')) return;
        try {
            setDeletingArtworkId(artworkId);
            await PrintItemService.deletePrintItemArtwork(id, artworkId);
            setExistingArtworks(existingArtworks.filter(a => a.id !== artworkId));
        } catch (err) {
            console.error("Error deleting artwork:", err);
            setError("Failed to delete artwork");
        } finally {
            setDeletingArtworkId(null);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const buildFormData = (data) => {
        const fd = new FormData();
        fd.append('title', data.title || '');
        fd.append('description', data.description || '');
        fd.append('htmlDescription', htmlDescription || '');
        fd.append('code', data.code || '');
        fd.append('imageUrl', currentImageUrl || '');
        fd.append('price', parseFloat(data.price) || 0);
        fd.append('cost', parseFloat(data.cost) || 0);
        fd.append('active', active ? 'true' : 'false');
        fd.append('pricingType', pricingType);
        fd.append('quantityPrices', JSON.stringify(pricingType === PRICING_TYPES.QUANTITY ? quantityPrices : []));
        if (file) {
            fd.append('imageFile', file);
        }
        additionalImages.forEach((img) => {
            fd.append('additionalImages', img);
        });
        artworkFiles.forEach((art) => {
            fd.append('artworkFiles', art);
        });
        return fd;
    };

    const onSubmit = async (data) => {
        try {
            setSaving(true);
            setError(null);

            const fd = buildFormData(data);
            if (isEditMode) {
                await PrintItemService.updatePrintItem(id, fd);
            } else {
                await PrintItemService.createPrintItem(fd);
            }
            navigate('/admin/printitems');
        } catch (err) {
            console.error("Error saving print item:", err);
            setError("Failed to save print item");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="py-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <div className="mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/admin/printitems')}
                    className="p-0 text-decoration-none"
                >
                    <ArrowLeft className="me-2" />
                    Back to Print Items
                </Button>
            </div>

            <h2 className="mb-4">{isEditMode ? 'Edit Print Item' : 'Add Print Item'}</h2>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    <Col md={4}>
                        <Card className="mb-4">
                            <Card.Body>
                                <Card.Title>Main Image</Card.Title>
                                <UploadLogo
                                    register={register}
                                    title="Main Image"
                                    fieldName="imageUrl"
                                    bgColor="#FFFFFF"
                                    currentLogo={currentImageUrl}
                                    setFile={setFile}
                                    width="100%"
                                />
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Body>
                                <Card.Title>Additional Images</Card.Title>

                                {existingImages.length > 0 && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-2">Saved Images</small>
                                        <div className="d-flex flex-wrap gap-2">
                                            {existingImages.map((img) => (
                                                <div key={img.id} className="position-relative" style={{ width: '80px' }}>
                                                    <img
                                                        src={getImageUrl(img.imageUrl)}
                                                        alt="Additional"
                                                        className="img-thumbnail"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                    />
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="position-absolute top-0 end-0 p-0"
                                                        style={{ width: '20px', height: '20px', fontSize: '10px' }}
                                                        onClick={() => deleteExistingImage(img.id)}
                                                        disabled={deletingImageId === img.id}
                                                    >
                                                        {deletingImageId === img.id ?
                                                            <Spinner size="sm" animation="border" style={{ width: '10px', height: '10px' }} /> :
                                                            <Trash />
                                                        }
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {additionalImages.length > 0 && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-2">New Images (to be uploaded)</small>
                                        <div className="d-flex flex-wrap gap-2">
                                            {additionalImages.map((newFile, index) => (
                                                <div key={index} className="position-relative" style={{ width: '80px' }}>
                                                    <img
                                                        src={URL.createObjectURL(newFile)}
                                                        alt={`New ${index + 1}`}
                                                        className="img-thumbnail"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                    />
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="position-absolute top-0 end-0 p-0"
                                                        style={{ width: '20px', height: '20px', fontSize: '10px' }}
                                                        onClick={() => removeAdditionalImage(index)}
                                                    >
                                                        <XCircle />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <Form.Group>
                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleAdditionalImagesChange}
                                        size="sm"
                                    />
                                    <Form.Text className="text-muted">
                                        Select multiple images to add
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Body>
                                <Card.Title>Print-Ready Artwork</Card.Title>
                                <Form.Text className="text-muted d-block mb-3">
                                    Large production files for the printer (PDF, AI, PSD, EPS, TIFF, ZIP, etc.). Links appear in the print purchase order spreadsheet.
                                </Form.Text>

                                {existingArtworks.length > 0 && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-2">Saved Artwork</small>
                                        <ul className="list-unstyled mb-0">
                                            {existingArtworks.map((art) => (
                                                <li key={art.id} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded">
                                                    <a
                                                        href={getImageUrl(art.fileUrl)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-truncate me-2"
                                                        title={art.originalName || art.fileUrl}
                                                    >
                                                        {art.originalName || 'Artwork file'}
                                                    </a>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => deleteExistingArtwork(art.id)}
                                                        disabled={deletingArtworkId === art.id}
                                                    >
                                                        {deletingArtworkId === art.id ?
                                                            <Spinner size="sm" animation="border" style={{ width: '12px', height: '12px' }} /> :
                                                            <Trash />
                                                        }
                                                    </Button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {artworkFiles.length > 0 && (
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-2">New Artwork (to be uploaded)</small>
                                        <ul className="list-unstyled mb-0">
                                            {artworkFiles.map((newFile, index) => (
                                                <li key={index} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded">
                                                    <span className="text-truncate me-2" title={newFile.name}>
                                                        {newFile.name}
                                                        <small className="text-muted ms-2">{formatFileSize(newFile.size)}</small>
                                                    </span>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => removeArtworkFile(index)}
                                                    >
                                                        <XCircle />
                                                    </Button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <Form.Group>
                                    <Form.Control
                                        type="file"
                                        accept=".pdf,.ai,.psd,.eps,.tif,.tiff,.png,.jpg,.jpeg,.zip,.rar,.svg"
                                        multiple
                                        onChange={handleArtworkFilesChange}
                                        size="sm"
                                    />
                                    <Form.Text className="text-muted">
                                        Up to 500 MB per file. Select multiple files.
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={8}>
                        <Card className="mb-4">
                            <Card.Body>
                                <Card.Title>Basic Information</Card.Title>

                                <Form.Group className="mb-3">
                                    <Form.Label>Title *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter item title"
                                        {...register('title', { required: true })}
                                    />
                                    {errors.title && <span className="text-danger">Title is required</span>}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Code / SKU</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., PRINT-001"
                                        {...register('code')}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Short Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Brief description"
                                        {...register('description')}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Full Description (HTML)</Form.Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={htmlDescription}
                                        onChange={setHtmlDescription}
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Body>
                                <Card.Title>Pricing</Card.Title>

                                <Form.Group className="mb-3">
                                    <Form.Label>Pricing Type</Form.Label>
                                    <Form.Select
                                        value={pricingType}
                                        onChange={(e) => setPricingType(parseInt(e.target.value))}
                                    >
                                        <option value={PRICING_TYPES.FLAT}>Flat Price</option>
                                        <option value={PRICING_TYPES.QUANTITY}>Quantity Pricing</option>
                                        <option value={PRICING_TYPES.BILL_LATER}>Bill Later</option>
                                    </Form.Select>
                                </Form.Group>

                                {pricingType === PRICING_TYPES.FLAT && (
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Price ($)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    {...register('price')}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Cost ($)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    {...register('cost')}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                {pricingType === PRICING_TYPES.QUANTITY && (
                                    <div className="mb-3">
                                        <Form.Label>Quantity Price Tiers</Form.Label>
                                        <Table size="sm" bordered>
                                            <thead>
                                                <tr>
                                                    <th>Quantity</th>
                                                    <th>Price ($)</th>
                                                    <th style={{ width: '60px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {quantityPrices.map((qp, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                size="sm"
                                                                min="1"
                                                                value={qp.quantity}
                                                                onChange={(e) => updateQuantityPrice(index, 'quantity', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                size="sm"
                                                                step="0.01"
                                                                min="0"
                                                                value={qp.price}
                                                                onChange={(e) => updateQuantityPrice(index, 'price', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            {quantityPrices.length > 1 && (
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="text-danger p-0"
                                                                    onClick={() => removeQuantityPrice(index)}
                                                                >
                                                                    <XCircle />
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={addQuantityPrice}
                                        >
                                            <PlusCircle className="me-1" /> Add Tier
                                        </Button>
                                    </div>
                                )}

                                {pricingType === PRICING_TYPES.BILL_LATER && (
                                    <Alert variant="info" className="mb-0">
                                        This item will be priced after the order is placed.
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>

                        <Card className="mb-4">
                            <Card.Body>
                                <Card.Title>Status</Card.Title>
                                <Form.Check
                                    type="switch"
                                    id="active-switch"
                                    label="Active (visible in catalog)"
                                    checked={active}
                                    onChange={(e) => setActive(e.target.checked)}
                                />
                            </Card.Body>
                        </Card>

                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    isEditMode ? 'Update Print Item' : 'Create Print Item'
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate('/admin/printitems')}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Container>
    );
};

export default AdminPrintItemForm;
